import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnInit, Output} from '@angular/core';
import {ToastrService} from 'ngx-toastr';
import {WebcamImage, WebcamInitError, WebcamUtil} from 'ngx-webcam';
import {Observable, Subject} from 'rxjs';

@Component({
  selector: 'app-validacao-documento-webcam',
  templateUrl: './validacao-documento-webcam.component.html',
  styleUrls: ['./validacao-documento-webcam.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ValidacaoDocumentoWebcamComponent implements OnInit {
  constructor(
    private cdr: ChangeDetectorRef,
    private toastr: ToastrService,
  ) {}
  @Output()
  public fotoCapturadaEmiter = new EventEmitter<WebcamImage>();

  @Output()
  public fotoConfirmadaPeloUsuarioEmiter = new EventEmitter<string>();

  @Output()
  public cancelled = new EventEmitter<void>();

  private trigger: Subject<void> = new Subject<void>();
  private nextWebcamSubject: Subject<boolean | string> = new Subject<boolean | string>();

  // alternar webcam ligada/desligada
  public mostrarWebcam = true;
  public permitirTrocaCamera = true;
  public multiplasWebcamsDisponiveis = false;
  public deviceId: string;
  public videoOptions: MediaTrackConstraints = {
    // Simplificado para evitar erros
    width: {ideal: 640},
    height: {ideal: 480},
    facingMode: 'user',
  };

  // Propriedades para o layout completo
  public errosWebcam: WebcamInitError[] = [];
  public imagemCapturada: WebcamImage | null = null;

  // Controle de step
  public step: 'permissao' | 'camera' | 'preview' | 'successo' | 'enviado' | 'final' | 'error' = 'permissao';
  public temPermissao: boolean = false;
  public verificandoPermissao: boolean = false;
  public validando: boolean = false;
  public erroValidacao: string = '';

  // Dicas para captura
  public dicas = [
    {type: 'ok', label: 'Boa iluminação'},
    {type: 'ok', label: 'Documento centralizado'},
    {type: 'ok', label: 'Sem reflexos'},
    {type: 'ok', label: 'Imagem nítida'},
  ];

  public ngOnInit(): void {
    this.checarpermissaoDispositivo();

    WebcamUtil.getAvailableVideoInputs()
      .then((mediaDevices: MediaDeviceInfo[]) => {
        this.multiplasWebcamsDisponiveis = mediaDevices && mediaDevices.length > 1;
      })
      .catch(error => {
        // Erro ao buscar dispositivos de vídeo
      });
  }

  private async checarpermissaoDispositivo(): Promise<void> {
    this.verificandoPermissao = true;
    this.erroValidacao = '';

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {facingMode: 'user'},
      });

      this.temPermissao = true;
      this.step = 'camera';

      stream.getTracks().forEach(track => track.stop());
    } catch (error: any) {
      if (error.name === 'NotAllowedError' || error.name === 'permissaoDeniedError') {
        this.erroValidacao =
          'Permissão de câmera negada. Por favor, permita o acesso à câmera nas configurações do navegador.';
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        this.erroValidacao = 'Nenhuma câmera encontrada. Verifique se há uma câmera conectada ao dispositivo.';
      } else {
        this.erroValidacao = 'Erro ao acessar a câmera. Verifique as configurações do navegador.';
      }

      this.temPermissao = false;
    } finally {
      this.verificandoPermissao = false;
    }
  }

  public aoSolicitarPermissao(): void {
    this.checarpermissaoDispositivo();
  }

  public aoCapturarImagem(webcamImage: WebcamImage): void {
    this.imagemCapturada = webcamImage;
    this.step = 'preview';
    this.fotoCapturadaEmiter.emit(webcamImage);
  }

  public aoIniciarErro(error: WebcamInitError): void {
    this.errosWebcam.push(error);
    setTimeout(() => {
      this.errosWebcam = []; // Limpar erros
    }, 2000);
  }

  public tirarFotoNovamente(): void {
    this.imagemCapturada = null;
    this.erroValidacao = '';
    this.step = 'camera';
  }

  public confirmarFoto(): void {
    if (!this.imagemCapturada) return;

    this.validando = true;
    this.erroValidacao = '';
    this.cdr.detectChanges();

    const mainTimeout = setTimeout(() => {
      this.validando = false;
      this.step = 'successo';

      // Emitir a imagem confirmada
      // this.fotoConfirmadaPeloUsuario.emit(this.imagemCapturada!.imageAsDataUrl);
      this.cdr.detectChanges();
    }, 1000);

    // Timeout de segurança - força completion se travar
    const safetyTimeout = setTimeout(() => {
      if (this.validando) {
        this.validando = false;
        this.step = 'successo';
        // this.fotoConfirmadaPeloUsuario.emit(this.imagemCapturada!.imageAsDataUrl);
        this.cdr.detectChanges();
      }
    }, 3000);

    // Limpar safety timeout quando completar normalmente
    setTimeout(() => {
      clearTimeout(safetyTimeout);
    }, 1100);
  }

  public cancelar(): void {
    this.cancelled.emit();
  }

  public enviarFotoParaAnaliseEmiter(): void {
    this.step = 'enviado';
    this.cdr.detectChanges();
    this.fotoConfirmadaPeloUsuarioEmiter.emit(this.imagemCapturada!.imageAsDataUrl);
  }

  public aoSelecionarArquivo(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const file = input.files[0];

    if (!file.type.startsWith('image/')) {
      this.erroValidacao = 'Por favor, selecione apenas imagens (JPG, PNG, GIF).';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;

      this.imagemCapturada = {imageAsDataUrl: base64} as WebcamImage;
      this.step = 'preview';
      this.erroValidacao = '';

      // Forçar atualização da UI
      this.cdr.detectChanges();

      // Limpar o input para permitir selecionar o mesmo arquivo novamente
      input.value = '';
    };

    reader.readAsDataURL(file);
  }

  public ehMobile(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  public dispararCaptura(): void {
    this.trigger.next();
  }

  public trocarCamera(): void {
    this.nextWebcamSubject.next(true);
  }

  public alternarWebcam(): void {
    this.mostrarWebcam = !this.mostrarWebcam;
  }

  public get triggerObservable(): Observable<void> {
    return this.trigger.asObservable();
  }

  public get switchCameraObservable(): Observable<boolean | string> {
    return this.nextWebcamSubject.asObservable();
  }

  // Métodos de utilidade
  obterTipoDispositivo(): string {
    const userAgent = navigator.userAgent;
    if (/iPad|iPhone|iPod/.test(userAgent)) return 'iOS';
    if (/Android/.test(userAgent)) return 'Android';
    if (/Windows/.test(userAgent)) return 'Windows';
    if (/Mac/.test(userAgent)) return 'Mac';
    return 'Desktop';
  }

  public mudarStep(step: any): void {
    this.step = step;
    this.cdr.detectChanges();
  }
}
